import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  message,
  Typography,
  Space,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import PageContainer from '../../../../components/shared/PageContainer';
import PageToolbar from '../../../../components/shared/PageToolbar';
import { DataTable, StatusTag } from '../../../../components/shared';
import {
  fetchNoteTemplates,
  deleteNoteTemplate,
} from '../../../../api/healthcare/TreatmentPlanApi';
import { TreatmentNoteTemplate } from '../../../../types/healthcare/TreatmentPlanTypes';
import NoteTemplateBuilderModal from './NoteTemplateBuilderModal';

const { Text } = Typography;

const NoteTemplatesPage: React.FC<{ embedded?: boolean }> = () => {

  const [templates, setTemplates] = useState<TreatmentNoteTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<TreatmentNoteTemplate | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchNoteTemplates();
      setTemplates(data);
    } catch {
      message.error('Failed to load note templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleReset = () => {
    setSearch('');
  };

	const handleOpenModal = (tpl: TreatmentNoteTemplate | null = null) => {
		setSelectedTemplate(tpl);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setSelectedTemplate(null);
		setIsModalOpen(false);
	};

  const handleDelete = (tpl: TreatmentNoteTemplate) => {
    if ((tpl as any).is_global) return; // guard — should not be reachable via UI
    Modal.confirm({
      title: `Delete "${tpl.name}"?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteNoteTemplate(tpl.id);
          message.success('Note template deleted');
          load();
        } catch (err: any) {
          message.error(err?.response?.data?.message ?? 'Delete failed');
        }
      },
    });
  };

  const filtered = templates.filter(
    (t) => !search || t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const fieldCount = (tpl: TreatmentNoteTemplate): number => {
    const structure = tpl.structure as any;
    if (Array.isArray(structure)) return (structure as any[]).filter((f: any) => f.type !== 'heading').length;
    return 0;
  };

  const columns: ColumnsType<TreatmentNoteTemplate> = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <Space size={6}>
          {(record as any).is_global && (
            <Tooltip title="Global template — managed by Xoe Med admin">
              <LockOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          )}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (desc) => desc ? <Text type="secondary">{desc}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Fields',
      width: 90,
      render: (_, record) => (
        <Text style={{ fontWeight: 600, color: 'var(--mg-primary)' }}>
          {fieldCount(record)}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 100,
      render: (active) => <StatusTag status={active ? 'active' : 'inactive'} />,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 140,
      render: (v) => <Text type="secondary">{dayjs(v).format('D MMM YYYY')}</Text>,
      sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => {
        const isGlobal = (record as any).is_global;
        return (
        <Space size={2}>
          <Tooltip title={isGlobal ? 'Global templates cannot be edited here' : 'Edit'}>
            <Button
              type="text"
              size="small"
              icon={isGlobal ? <LockOutlined /> : <EditOutlined />}
              disabled={isGlobal}
              onClick={(e) => {
                e.stopPropagation();
                if (!isGlobal) handleOpenModal(record as TreatmentNoteTemplate);
              }}
            />
          </Tooltip>
          <Tooltip title={isGlobal ? 'Global templates cannot be deleted here' : 'Delete'}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={isGlobal}
              onClick={(e) => {
                e.stopPropagation();
                if (!isGlobal) handleDelete(record);
              }}
            />
          </Tooltip>
        </Space>
        );
      },
    },
  ];

  const content = (
    <PageContainer>
      <PageToolbar
        primaryAction={{
          label: 'New Template',
          icon: <PlusOutlined />,
          onClick: () => {
						handleOpenModal();
					},
        }}
        filters={[
          {
            type: 'search',
            placeholder: 'Search templates…',
            value: search,
            onChange: setSearch,
            width: 260,
          },
        ]}
        onReset={handleReset}
      />
      <DataTable
        columns={columns}
        dataSource={filtered}
        rowKey="id"
        loading={loading}
        onRowClick={(record) => {
          if (!(record as any).is_global) handleOpenModal(record as TreatmentNoteTemplate);
        }}
        emptyTitle="No note templates yet"
        emptyDescription="Create your first note template to get started"
        emptyAction={{
          label: 'New Template',
          onClick: () => {
						handleOpenModal();
					},
        }}
      />
			{isModalOpen && (
				<NoteTemplateBuilderModal
					visible={isModalOpen}
					template={selectedTemplate}
					onClose={handleCloseModal}
				/>
			)}

    </PageContainer>
  );

  return content;
};

export default NoteTemplatesPage;
